import vertexShader from "./fluid-light.vert";
import fluidLightShader from "./fluid-light.frag";

import { Camera, Color, OGLRenderingContext, Program, Vec2 } from "ogl";
import {
  easeInOutCubic,
  createMesh,
  createGeometry,
  mergeDeep,
} from "../../../utils";
import {
  BaseShader,
  BaseShaderConfig,
  createWebGLSetup,
} from "../../base-shader";

export interface FluidLightEchoConfig {
  enabled: boolean;
  offset: number;
  radius: number;
  frequency: number;
  ringSize: number;
  strokeOpacity: number;
  fillOpacity: number;
  animateIn: boolean;
  animationDuration: number;
  animationDelay: number;
}

export interface FluidLightLightWaveConfig {
  amplitude: number;
  speed: number;
  frequency: number;
  color1: string;
  color2: string;
  color3: string;
  color4: string;
  color5: string;
  minFalloff: number;
  maxFalloff: number;
}

export interface FluidLightConfig extends BaseShaderConfig {
  side: number;
  background: string;
  lightWave: FluidLightLightWaveConfig;
  echo: FluidLightEchoConfig;
}

export interface FluidLightShader extends BaseShader {
  updateParams(params: Partial<FluidLightConfig>): void;
  reset(): void;
  exportConfig(): FluidLightConfig;
  destroy(): void;
  getParams(): FluidLightConfig;
}

const FLUID_LIGHT_DEFAULT_CONFIG: FluidLightConfig = {
  side: 0,
  background: "#020617",
  lightWave: {
    amplitude: 0.04,
    speed: 0.4,
    frequency: 1.2,
    color1: "#5648FB",
    color2: "#3AC3FF",
    color3: "#CE7FFF",
    color4: "#F45397",
    color5: "#FFB525",
    minFalloff: 6.0,
    maxFalloff: 8.0,
  },
  echo: {
    enabled: true,
    offset: 0.1,
    radius: 0.9,
    frequency: 20.0,
    ringSize: 2.0,
    strokeOpacity: 0.2,
    fillOpacity: 0.01,
    animateIn: true,
    animationDuration: 3000,
    animationDelay: 200,
  },
};

export async function createFluidLight(
  canvas: HTMLCanvasElement,
  config: Partial<FluidLightConfig> = {}
): Promise<FluidLightShader> {
  const { renderer, gl } = createWebGLSetup(canvas, "FluidLight");

  const mergedConfig = mergeDeep(FLUID_LIGHT_DEFAULT_CONFIG, config);

  const params: FluidLightConfig = JSON.parse(
    JSON.stringify({
      side: mergedConfig.side,
      background: mergedConfig.background,
      lightWave: mergedConfig.lightWave,
      echo: mergedConfig.echo,
    })
  );
  const defaultConfig: FluidLightConfig = JSON.parse(JSON.stringify(params));

  const resolution = new Vec2();
  const camera = new Camera(gl, { fov: 35 });
  camera.position.set(0, 0, 5);

  const cursorPosition = new Vec2();
  const currentOffset = new Vec2();
  let animationStartTime = performance.now();

  function updateCanvasSize() {
    // parentElement is a temporary workaround
    // https://github.com/oframe/ogl/issues/256
    const width = canvas.parentElement?.clientWidth ?? canvas.clientWidth;
    const height = canvas.clientHeight;

    renderer.setSize(width, height);
    camera.perspective({ aspect: width / height });
    resolution.set(width, height);
  }

  function onMouseMove(event: MouseEvent) {
    const rect = canvas.getBoundingClientRect();
    cursorPosition.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    cursorPosition.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  function onTouchMove(event: TouchEvent) {
    const rect = canvas.getBoundingClientRect();
    const touch = event.touches[0];
    cursorPosition.x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
    cursorPosition.y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;
  }

  canvas.addEventListener("mousemove", onMouseMove);
  canvas.addEventListener("touchmove", onTouchMove);
  updateCanvasSize();

  const fluidLightProgram = createFluidLightProgram(gl, params, resolution);

  const geometry = createGeometry(gl);
  const fluidLightPlane = createMesh(gl, geometry, fluidLightProgram);

  let rafId: number;
  function update(t: number) {
    rafId = requestAnimationFrame(update);

    updateCanvasSize();
    updateFluidLightUniforms(t);

    renderer.render({
      scene: fluidLightPlane,
      camera,
    });
  }

  function updateFluidLightUniforms(t: number) {
    const bgColor = new Color(params.background);
    fluidLightProgram.uniforms.uBackground.value = [
      bgColor.r,
      bgColor.g,
      bgColor.b,
      1,
    ];

    // Light wave uniforms
    fluidLightProgram.uniforms.uTime.value = t * 0.001;
    fluidLightProgram.uniforms.uResolution.value = resolution;
    fluidLightProgram.uniforms.uWaveAmplitude.value =
      params.lightWave.amplitude;
    fluidLightProgram.uniforms.uWaveSpeed.value = params.lightWave.speed;
    fluidLightProgram.uniforms.uWaveFrequency.value =
      params.lightWave.frequency;
    fluidLightProgram.uniforms.uWaveSide.value = params.side;
    fluidLightProgram.uniforms.uWaveColor1.value = new Color(
      params.lightWave.color1
    );
    fluidLightProgram.uniforms.uWaveColor2.value = new Color(
      params.lightWave.color2
    );
    fluidLightProgram.uniforms.uWaveColor3.value = new Color(
      params.lightWave.color3
    );
    fluidLightProgram.uniforms.uWaveColor4.value = new Color(
      params.lightWave.color4
    );
    fluidLightProgram.uniforms.uWaveColor5.value = new Color(
      params.lightWave.color5
    );
    fluidLightProgram.uniforms.uMinFalloff.value = params.lightWave.minFalloff;
    fluidLightProgram.uniforms.uMaxFalloff.value = params.lightWave.maxFalloff;

    // Echo uniforms
    fluidLightProgram.uniforms.uEchoEnabled.value = params.echo.enabled;
    fluidLightProgram.uniforms.uEchoOffset.value = params.echo.offset;
    fluidLightProgram.uniforms.uEchoRadius.value = params.echo.radius;
    fluidLightProgram.uniforms.uEchoFrequency.value =
      getAnimatedEchoFrequency(t);
    fluidLightProgram.uniforms.uEchoRingSize.value = params.echo.ringSize;
    fluidLightProgram.uniforms.uEchoStrokeOpacity.value =
      getAnimatedEchoStrokeOpacity(t);
    fluidLightProgram.uniforms.uEchoFillOpacity.value = params.echo.fillOpacity;
  }

  function getAnimatedEchoFrequency(t: number): number {
    if (!params.echo.animateIn) {
      return params.echo.frequency;
    }

    const elapsed = t - animationStartTime;
    const delay = params.echo.animationDelay;
    const duration = params.echo.animationDuration;

    if (elapsed < delay) {
      return 0;
    }

    const progress = Math.min((elapsed - delay) / duration, 1);
    const easedProgress = easeInOutCubic(progress);
    return params.echo.frequency * easedProgress;
  }

  function getAnimatedEchoStrokeOpacity(t: number): number {
    if (!params.echo.animateIn) {
      return params.echo.strokeOpacity;
    }

    const elapsed = t - animationStartTime;
    const delay = params.echo.animationDelay;
    const duration = params.echo.animationDuration;

    if (elapsed < delay) {
      return 0;
    }

    const progress = Math.min((elapsed - delay) / duration, 1);
    const easedProgress = easeInOutCubic(progress);
    return params.echo.strokeOpacity * easedProgress;
  }

  function updateParams(newParams: Partial<FluidLightConfig>) {
    Object.assign(params, newParams);
  }

  function reset() {
    Object.assign(params, JSON.parse(JSON.stringify(defaultConfig)));
    animationStartTime = performance.now();
  }

  function exportConfig(): FluidLightConfig {
    return JSON.parse(JSON.stringify(params));
  }

  function destroy() {
    canvas.removeEventListener("mousemove", onMouseMove);
    canvas.removeEventListener("touchmove", onTouchMove);
    cancelAnimationFrame(rafId);
  }

  function getParams(): FluidLightConfig {
    return params;
  }

  update(performance.now());

  return {
    updateParams,
    reset,
    exportConfig,
    destroy,
    getParams,
  };
}

function createFluidLightProgram(
  gl: OGLRenderingContext,
  params: FluidLightConfig,
  resolution: Vec2
) {
  return new Program(gl, {
    vertex: vertexShader,
    fragment: fluidLightShader,
    uniforms: {
      uBackground: { value: [0, 0, 0, 1] },
      uResolution: { value: resolution },
      uTime: { value: 0 },
      uWaveAmplitude: { value: params.lightWave.amplitude },
      uWaveSpeed: { value: params.lightWave.speed },
      uWaveFrequency: { value: params.lightWave.frequency },
      uWaveSide: { value: params.side },
      uWaveColor1: { value: new Color(params.lightWave.color1) },
      uWaveColor2: { value: new Color(params.lightWave.color2) },
      uWaveColor3: { value: new Color(params.lightWave.color3) },
      uWaveColor4: { value: new Color(params.lightWave.color4) },
      uWaveColor5: { value: new Color(params.lightWave.color5) },
      uMinFalloff: { value: params.lightWave.minFalloff },
      uMaxFalloff: { value: params.lightWave.maxFalloff },
      uEchoEnabled: { value: params.echo.enabled },
      uEchoOffset: { value: params.echo.offset },
      uEchoRadius: { value: params.echo.radius },
      uEchoFrequency: { value: params.echo.frequency },
      uEchoRingSize: { value: params.echo.ringSize },
      uEchoStrokeOpacity: { value: params.echo.strokeOpacity },
      uEchoFillOpacity: { value: params.echo.fillOpacity },
    },
  });
}
